import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  _lastCartUpdateTs = 0;

  /** @type {number} */
  _lastCartUpdateCount = -1;

  /** @type {number | undefined} */
  _cartBubbleAnimationTimer = undefined;

  /** @type {number | undefined} */
  _cartBubbleAnimationFrame = undefined;

  /** @type {number} */
  get currentCartCount() {
    const txt = (this.refs.cartBubbleCount?.textContent ?? '0').trim();
    const n = parseInt(txt === '' ? '0' : txt, 10);
    return Number.isFinite(n) ? n : 0;
  }

  set currentCartCount(value) {
    const v = Math.max(0, Number(value) || 0);
    this.refs.cartBubbleCount.textContent = v < 100 ? String(v) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // EG: complementary / wishlist custom events
    document.addEventListener('eg:cart:added', this.onEgAdded);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);

    // EG: complementary / wishlist custom events
    document.removeEventListener('eg:cart:added', this.onEgAdded);

    this.cancelCartBubbleAnimationCleanup();
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Extracts cart payload from different event shapes used across the theme/apps.
   * Supports:
   * - detail.data.itemCount            -> delta for product-form
   * - detail.data.cart.item_count      -> absolute count
   * - detail.cart.item_count           -> absolute count
   * - detail.itemCount                 -> absolute or delta depending on source
   * - detail.quantity                  -> delta fallback
   */
  extractCartPayload = (event) => {
    const detail = event?.detail || {};
    const data = detail?.data || {};
    const source = data.source ?? detail.source ?? '';
    const didError = Boolean(data.didError ?? detail.didError);

    const absoluteCandidates = [
      data?.cart?.item_count,
      detail?.cart?.item_count,
      detail?.resource?.item_count,
    ];

    let absoluteCount = null;
    for (const candidate of absoluteCandidates) {
      const n = Number(candidate);
      if (Number.isFinite(n)) {
        absoluteCount = n;
        break;
      }
    }

    if (didError && absoluteCount == null) {
      return {
        source,
        absoluteCount: null,
        deltaCount: null,
      };
    }

    let deltaCount = null;

    if (source === 'product-form-component') {
      const rawDelta =
        data?.itemCount ??
        detail?.itemCount ??
        data?.quantity ??
        detail?.quantity;

      const n = Number(rawDelta);
      if (Number.isFinite(n)) {
        deltaCount = n;
      }
    } else {
      if (absoluteCount == null) {
        const rawAbsolute = data?.itemCount ?? detail?.itemCount;
        const n = Number(rawAbsolute);
        if (Number.isFinite(n)) {
          absoluteCount = n;
        }
      }

      const rawDelta = data?.quantity ?? detail?.quantity;
      const n = Number(rawDelta);
      if (Number.isFinite(n)) {
        deltaCount = n;
      }
    }

    return {
      source,
      absoluteCount,
      deltaCount,
    };
  };

  onEgAdded = (event) => {
    const { source, absoluteCount, deltaCount } = this.extractCartPayload(event);
    const hasAbsoluteCount = Number.isFinite(absoluteCount);
    const hasDeltaCount = Number.isFinite(deltaCount);

    if (!hasAbsoluteCount && !hasDeltaCount) {
      return;
    }

    const now = Date.now();

    // eg-wishlist-cart-actions-v2 dispatches the native theme cart:update first,
    // then eg:cart:added. Do not rebroadcast cart:update here, otherwise the cart
    // bubble update runs twice for the same add-to-cart action.
    if (
      hasAbsoluteCount &&
      now - (this._lastCartUpdateTs || 0) < 350 &&
      Number(absoluteCount) === this._lastCartUpdateCount
    ) {
      return;
    }

    if (hasAbsoluteCount) {
      this.renderCartBubble(Number(absoluteCount), false, true);
      return;
    }

    const qty = Number(deltaCount ?? 1) || 1;

    if (
      source === 'wishlist-drawer-add-all' &&
      now - (this._lastCartUpdateTs || 0) < 350
    ) {
      return;
    }

    this.renderCartBubble(qty, true, true);
  };

  onCartUpdate = (event) => {
    this._lastCartUpdateTs = Date.now();

    const { source, absoluteCount, deltaCount } = this.extractCartPayload(event);

    const hasAbsoluteCount = Number.isFinite(absoluteCount);
    const hasDeltaCount = Number.isFinite(deltaCount);

    if (!hasAbsoluteCount && !hasDeltaCount) {
      return;
    }

    const itemCount = hasAbsoluteCount
      ? Number(absoluteCount)
      : hasDeltaCount
        ? Number(deltaCount)
        : 0;

    const comingFromProductForm =
      !hasAbsoluteCount &&
      source === 'product-form-component' &&
      hasDeltaCount;

    const nextCount = comingFromProductForm
      ? Math.max(0, this.currentCartCount + itemCount)
      : Math.max(0, itemCount);

    this._lastCartUpdateCount = nextCount;
    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  cancelCartBubbleAnimationCleanup = () => {
    if (this._cartBubbleAnimationFrame) {
      cancelAnimationFrame(this._cartBubbleAnimationFrame);
      this._cartBubbleAnimationFrame = undefined;
    }

    if (this._cartBubbleAnimationTimer) {
      clearTimeout(this._cartBubbleAnimationTimer);
      this._cartBubbleAnimationTimer = undefined;
    }
  };

  scheduleCartBubbleAnimationCleanup = () => {
    this.cancelCartBubbleAnimationCleanup();

    this._cartBubbleAnimationTimer = window.setTimeout(() => {
      this._cartBubbleAnimationTimer = undefined;
      this.refs.cartBubble?.classList.remove('cart-bubble--animating');
    }, 420);
  };

  renderCartBubble = (itemCount, comingFromProductForm, animate = true) => {
    const currentCount = this.currentCartCount;
    const nextCount = comingFromProductForm
      ? Math.max(0, currentCount + (Number(itemCount) || 0))
      : Math.max(0, Number(itemCount) || 0);
    const hasItems = nextCount > 0;

    this.currentCartCount = nextCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', !hasItems);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasItems);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    this.cancelCartBubbleAnimationCleanup();

    if (!animate || !hasItems) {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
      return;
    }

    // Avoid onAnimationEnd()/getAnimations() here. getAnimations() can force a
    // synchronous style/layout flush immediately after the DOM changes above.
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    this.scheduleCartBubbleAnimationCleanup();
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    if (Date.now() - (this._lastCartUpdateTs || 0) < 1500) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
